import { Box, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type Employee from '@src/Objects/Employee';
import {
    Award,
    BarChart3,
    Calendar,
    CalendarPlus,
    Edit,
    HandMetal,
    MessageSquare,
    Plus,
    Search,
    Target,
    TrendingUp,
    Upload,
    Zap,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

type TabContext = 'overview' | 'skills' | 'certificates' | 'development' | 'forecast' | 'profile';
type LayoutMode = 'horizontal' | 'vertical';

interface QuickLinksWidgetNewProps {
    employee: Employee;
    onTabChange: (tabIndex: number) => void;
    tabContext?: TabContext;
    layoutMode?: LayoutMode;
}

interface QuickLink {
    title: string;
    description: string;
    icon: React.ReactNode;
    action: () => void;
    contexts: TabContext[];
}

function QuickLinksWidgetNew({
    onTabChange,
    tabContext = 'overview',
    layoutMode = 'horizontal',
}: QuickLinksWidgetNewProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    const allQuickLinks: QuickLink[] = [
        {
            title: 'Profil bearbeiten',
            description: 'Persönliche Daten aktualisieren',
            icon: <Edit size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT}`),
            contexts: ['overview', 'profile'],
        },
        {
            title: 'Skills hinzufügen',
            description: 'Neue Fähigkeiten erfassen',
            icon: <Plus size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`),
            contexts: ['overview', 'skills'],
        },
        {
            title: 'Skills verwalten',
            description: 'Bestehende Skills bearbeiten',
            icon: <HandMetal size={20} strokeWidth={1.5} />,
            action: () => onTabChange(5),
            contexts: ['skills'],
        },
        {
            title: 'Skill-Vorschläge',
            description: 'AI-basierte Empfehlungen',
            icon: <TrendingUp size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_SKILLS_SUGGESTIONS}`),
            contexts: ['skills'],
        },
        {
            title: 'Zertifikat hinzufügen',
            description: 'Neues Zertifikat hochladen',
            icon: <Upload size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`),
            contexts: ['overview', 'certificates'],
        },
        {
            title: 'Zertifikate verwalten',
            description: 'Bestehende Zertifikate bearbeiten',
            icon: <Award size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`),
            contexts: ['certificates'],
        },
        {
            title: 'Entwicklungsziel hinzufügen',
            description: 'Neues Ziel definieren',
            icon: <Plus size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
            contexts: ['development'],
        },
        {
            title: 'Ziele verwalten',
            description: 'Karriereplanung bearbeiten',
            icon: <Target size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
            contexts: ['development'],
        },
        {
            title: 'Verfügbarkeit anpassen',
            description: 'Arbeitszeiten aktualisieren',
            icon: <Calendar size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_FORECAST}`),
            contexts: ['forecast'],
        },
        {
            title: 'Forecast analysieren',
            description: 'Auslastung visualisieren',
            icon: <BarChart3 size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_FORECAST}`),
            contexts: ['forecast'],
        },
        {
            title: 'CV analysieren',
            description: 'AI-gestützte Profilanalyse',
            icon: <TrendingUp size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_ANALYSE}`),
            contexts: ['overview'],
        },
        {
            title: 'Mitarbeiter suchen',
            description: 'Kollegen mit Skills finden',
            icon: <Search size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
            contexts: ['overview'],
        },
        {
            title: 'Verfügbarkeit aktualisieren',
            description: 'Arbeitszeiten/Projekte anpassen',
            icon: <Calendar size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_FORECAST}`),
            contexts: ['overview'],
        },
        {
            title: 'Entwicklungsziele setzen',
            description: 'Karriereplanung',
            icon: <Target size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT}`),
            contexts: ['overview'],
        },
        {
            title: 'Feedback geben',
            description: 'Bewertungen/Empfehlungen abgeben',
            icon: <MessageSquare size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
            contexts: ['overview'],
        },
        {
            title: 'Termine planen',
            description: 'Meetings/Gespräche organisieren',
            icon: <CalendarPlus size={20} strokeWidth={1.5} />,
            action: () => navigate(`/${ELinks.EMPLOYEE_TALKS}`),
            contexts: ['overview'],
        },
    ];

    const quickLinks = allQuickLinks.filter((link) => link.contexts.includes(tabContext));

    if (quickLinks.length === 0) return null;

    const isHorizontal = layoutMode === 'horizontal';

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1.5px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.divider, 0.2)}`,
                bgcolor: 'background.paper',
                ...(!isHorizontal && {
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                    },
                    transition: 'all 0.2s ease',
                }),
            }}
        >
            <Box sx={{ p: { lg: 3, md: 2, xs: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Zap size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Quick Links
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{ opacity: 0.8, mb: 3, fontSize: '0.8rem' }}>
                    {isHorizontal ? 'Schnelle Aktionen für dein Profil' : 'Aktionen'}
                </Typography>

                <Box
                    sx={
                        isHorizontal
                            ? {
                                  display: 'flex',
                                  gap: 2,
                                  overflowX: 'auto',
                                  overflowY: 'hidden',
                                  pb: 1,
                                  WebkitOverflowScrolling: 'touch',
                                  scrollBehavior: 'smooth',
                                  '&::-webkit-scrollbar': {
                                      display: 'none',
                                  },
                                  scrollbarWidth: 'none',
                              }
                            : {
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1.5,
                              }
                    }
                >
                    {quickLinks.map((link, index) => (
                        <Paper
                            key={index}
                            elevation={0}
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: isHorizontal ? 'column' : 'row',
                                alignItems: 'center',
                                textAlign: isHorizontal ? 'center' : 'left',
                                justifyContent: isHorizontal ? 'center' : 'flex-start',
                                minWidth: isHorizontal ? 140 : 'auto',
                                minHeight: isHorizontal ? 100 : 60,
                                flex: isHorizontal ? '0 0 auto' : '1',
                                gap: isHorizontal ? 0 : 1.5,
                                background: alpha(theme.palette.primary.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderColor: theme.palette.primary.main,
                                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                                },
                            }}
                            onClick={link.action}
                        >
                            <Box
                                sx={{
                                    color: theme.palette.primary.main,
                                    mb: isHorizontal ? 1 : 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {link.icon}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isHorizontal ? 'center' : 'flex-start',
                                    textAlign: isHorizontal ? 'center' : 'left',
                                    flex: 1,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 0.5,
                                        fontSize: isHorizontal ? '0.75rem' : '0.8rem',
                                        color: 'text.primary',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {link.title}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        opacity: 0.7,
                                        fontSize: isHorizontal ? '0.65rem' : '0.7rem',
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {link.description}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
}

export default QuickLinksWidgetNew;
